

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { getLungsCareMainData } from './SymptomCheckerapi';
import CarouselSlider from '../../Shared/CarouselSlider/CarouselSlider';
import IntroSection from './IntroSection';
import SecondSection from '../../Shared/SecondSection/SecondSection';
import CurebaySolutions from '../../Shared/CurebaySolutions/CurebaySolutions';
import ThirdSection from '../../Shared/ThirdSection/ThirdSection';
import CheckHealthSteps from '../../Shared/CheckHealthSteps/CheckHealthSteps';
import ConcernsSection from '../../Shared/ConcernsSection/ConcernsSection';
import { usePatientData } from '../../hooks/usePatientData';
import { FeedbackModal, useServicePageFeedback } from '../Home/FeedbackSystem';
import LoginToast from '../../MainLayout/ToastifyLogin';
import { LoginContext } from '../../MainLayout/MainLayout';

// Dialog import removed — chat now lives at /symptomchecker/chat

const SymptomChecker = () => {
  const { openLogin } = useContext(LoginContext);
  const storedData1 = JSON.parse(localStorage.getItem("loginObj"));
  const userData = storedData1;
  const { showFeedback, closeFeedback } = useServicePageFeedback(userData?.code, 'Symptom Checker');
  const loginToastRef = useRef(null);
  const [attrs, setAttrs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toastRef = useRef(null);
  const [report_count, setReportCount] = useState(null);
  const endpointBase = process.env.REACT_APP_REPORTCOUNTURL;
  const countApiUrl = `${endpointBase}symptomchecker/triages/count`;
  const { patientData } = usePatientData();

  const navigate = useNavigate();

  const handleCardClick = (item) => {
    const subheading = item?.subheading?.toLowerCase() || "";

    if (subheading === "visioncare") {
      navigate("/visioncare");
      return;
    }

    if (subheading === "caresathi")          navigate("/caresathi");
    else if (subheading === "skincare")      navigate("/skincare");
    else if (subheading === "smartvitals")   navigate("/smartvital");
    else if (subheading === "oralcare")      navigate("/oralcare");
    else if (subheading === "lungscare")     navigate("/lungscare");
    else if (subheading === "smart report")  navigate("/smartreport");
    else if (subheading === "symptomchecker") navigate("/symptomchecker");
    else if (subheading === "smart diet")    navigate("/smartdiet");
    else console.warn("No route found for:", subheading);
  };

  // Navigate to the dedicated chat page — requires login
  const handleOpen = (e) => {
    if (!userData?.code) {
      e.preventDefault();
      openLogin();
      return;
    }
    // ← The only change: navigate instead of opening a Dialog
navigate("/symptomchecker/chat?new=1");  };

  useEffect(() => {
    let isMounted = true;
    getLungsCareMainData()
      .then(res => {
        if (!isMounted) return;
        const entry = res.data?.data?.[0]?.attributes;
        setAttrs(entry || {});
      })
      .catch(err => { if (!isMounted) return; setError(err); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!countApiUrl) return;
    const fetchCount = async () => {
      try {
        const res = await fetch(countApiUrl);
        const data = await res.json();
        setReportCount(data.triages ?? null);
      } catch (err) {
        console.error("Failed to fetch report count:", err);
      }
    };
    fetchCount();
  }, [countApiUrl]);

  if (loading) return <div className="p-8 text-center">Loading…</div>;
  if (error)   return <div className="p-8 text-center text-red-600">Error loading data.</div>;
  if (!attrs)  return <div className="p-8 text-center">No data</div>;

  const firstcompItem = attrs.first?.[0] || {};
  const heading     = firstcompItem.heading     || '';
  const subheading  = firstcompItem.subheading  || '';
  const description = firstcompItem.description || '';
  const image       = firstcompItem.imgurl      || '';
  const button      = firstcompItem.button      || '';
  const button1     = firstcompItem.button1     || '';

  const firstSectionItems = [{
    heading, subheading, description, button,
    onButtonClick: handleOpen,
    image,
  }];

  const secondcompArr = attrs.second?.[0]?.secsub || [];
  const thirdSection  = attrs.third?.[0]  || {};
  const fourthSection = attrs.fourth?.[0] || {};
  const fifthSection  = attrs.fifth?.[0]  || {};
  const sixthSection  = attrs.sixth?.[0]  || {};

  return (
    <>
      <LoginToast ref={loginToastRef} />
      <Toast ref={toastRef} position="top-right" />

      <section>
        {/* Animated background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#42BA85]/8 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-32 right-16 w-96 h-96 bg-gradient-to-tr from-[#F3F8FF]/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-br from-[#42BA85]/6 to-transparent rounded-full blur-2xl animate-bounce" style={{ animationDelay: '2s', animationDuration: '3s' }} />
        </div>

        <IntroSection items={firstSectionItems} report_count={report_count} type="triage" />

        <section className="bg-[#D9F1E7]">
          <SecondSection items={secondcompArr} />
        </section>

        {Object.keys(thirdSection).length  > 0 && <ThirdSection data={thirdSection} />}
        {Object.keys(fourthSection).length > 0 && <CheckHealthSteps data={fourthSection} onButtonClick={handleOpen} />}
        {Object.keys(fifthSection).length  > 0 && <ConcernsSection data={fifthSection} />}
        {Object.keys(sixthSection).length  > 0 && <CurebaySolutions data={sixthSection} onCardClick={handleCardClick} />}
      </section>

      {/* Dialog removed — SymptomCheckerChat renders at /symptomchecker/chat */}
      {showFeedback && (
        <FeedbackModal service="Symptom Checker" patientId={userData?.code} onClose={closeFeedback} />
      )}
    </>
  );
};

export default SymptomChecker;